import { useEffect, useCallback } from 'react';
import { Mic, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { VoiceState } from '@/types/search';
import { cn } from '@/lib/utils';

interface VoiceSearchOverlayProps {
  isOpen: boolean;
  voiceState: VoiceState;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  onClose: () => void;
  onSearch: (query: string) => void;
}

export const VoiceSearchOverlay = ({
  isOpen,
  voiceState,
  transcript,
  interimTranscript,
  error,
  onClose,
  onSearch,
}: VoiceSearchOverlayProps) => {
  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Auto-search when transcript is final
  useEffect(() => {
    if (voiceState === 'idle' && transcript && !interimTranscript) {
      const timer = setTimeout(() => {
        onSearch(transcript);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [voiceState, transcript, interimTranscript, onSearch]);

  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  if (!isOpen) return null;

  const displayText = transcript + (interimTranscript ? ` ${interimTranscript}` : '');

  return (
    <div 
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-label="Búsqueda por voz"
    >
      <div className="flex flex-col items-center gap-8 p-8 max-w-lg w-full">
        {/* Close button */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="absolute top-4 right-4"
          aria-label="Cerrar búsqueda por voz"
        >
          <X className="h-6 w-6" />
        </Button>

        {/* Microphone with animation */}
        <div className="relative">
          <div
            className={cn(
              "w-24 h-24 rounded-full flex items-center justify-center transition-all duration-300",
              voiceState === 'listening' 
                ? "bg-destructive/20 animate-pulse" 
                : voiceState === 'processing'
                ? "bg-primary/20"
                : voiceState === 'error'
                ? "bg-destructive/10"
                : "bg-muted"
            )}
          >
            {voiceState === 'processing' ? (
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
            ) : (
              <Mic 
                className={cn(
                  "w-12 h-12 transition-colors",
                  voiceState === 'listening' ? "text-destructive" : "text-muted-foreground"
                )} 
              />
            )}
          </div>
          
          {/* Waveform animation */}
          {voiceState === 'listening' && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 bg-destructive rounded-full animate-pulse"
                    style={{
                      height: `${20 + Math.random() * 20}px`,
                      animationDelay: `${i * 0.1}s`,
                      animationDuration: `${0.5 + Math.random() * 0.5}s`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Status text */}
        <div className="text-center space-y-2">
          <p className="text-lg font-medium">
            {voiceState === 'listening' 
              ? 'Escuchando...' 
              : voiceState === 'processing'
              ? 'Procesando...'
              : voiceState === 'error'
              ? 'Error'
              : 'Listo para escuchar'}
          </p>
          
          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>

        {/* Transcript display */}
        {displayText && (
          <div className="w-full p-4 bg-muted rounded-lg min-h-[60px]">
            <p className="text-lg text-center">
              <span>{transcript}</span>
              {interimTranscript && (
                <span className="text-muted-foreground opacity-70">{interimTranscript}</span>
              )}
            </p>
          </div>
        )}

        {/* Action buttons */}
        <div className="flex gap-4">
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          {transcript && (
            <Button onClick={() => onSearch(transcript)}>
              Buscar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
